// F170 (Tomas Pohl 2026-10-05): podněty -> targeted system-prompt patch.
// One vocabulary for the Worker (staff + machine routes) and the SPA card, so
// a status, a category or a limit can never drift between the two.
//
// Flow: queued -> proposing (Mini claimed it) -> proposed (before/after +
// per-podnět category) -> approved | rejected (partner) -> applying (Mini
// claimed the apply) -> applied | failed.

export const PROMPT_PATCH_STATUSES = [
  "queued", "proposing", "proposed", "approved", "applying", "applied", "rejected", "failed"
] as const;
export type PromptPatchStatus = typeof PROMPT_PATCH_STATUSES[number];

/** Statuses that block a second request for the same client. */
export const PROMPT_PATCH_OPEN_STATUSES: readonly PromptPatchStatus[] = ["queued", "proposing", "proposed", "approved", "applying"];
/** Statuses during which the card polls (the machine owes the next move). */
export const PROMPT_PATCH_POLL_STATUSES: readonly PromptPatchStatus[] = ["queued", "proposing", "approved", "applying"];

export const PROMPT_PATCH_CATEGORIES = ["prompt", "knowhow", "platform", "irrelevant"] as const;
export type PromptPatchCategory = typeof PROMPT_PATCH_CATEGORIES[number];

export const PROMPT_PATCH_LIMITS = {
  minFeedback: 1,
  maxFeedback: 20,
  noteChars: 2000,
  maxPatches: 30,
  patchTextChars: 20000,
  reasonChars: 2000,
  summaryChars: 4000,
  resultNoteChars: 2000,
  /** A claim older than this may be taken over by another runner pass. */
  leaseMs: 30 * 60_000
} as const;

export interface PromptPatchItem { feedbackId: string; category: PromptPatchCategory; reason: string }
export interface PromptPatchHunk { find: string; replace: string; why: string }
export interface PromptPatchProposal { items: PromptPatchItem[]; patch: PromptPatchHunk[]; summary: string }
export interface PromptPatchEdit { find: string; replace: string }

/** What the staff card receives. No lease token, no actor ids. */
export interface PromptPatchRequestDto {
  id: string;
  status: PromptPatchStatus;
  feedbackIds: string[];
  partnerNote: string | null;
  proposal: PromptPatchProposal | null;
  editedPatch: PromptPatchEdit[] | null;
  resultNote: string | null;
  createdAt: string;
  updatedAt: string;
  decidedAt: string | null;
}

export function isPromptPatchStatus(value: unknown): value is PromptPatchStatus {
  return typeof value === "string" && (PROMPT_PATCH_STATUSES as readonly string[]).includes(value);
}

export function isOpenPromptPatch(status: PromptPatchStatus): boolean {
  return PROMPT_PATCH_OPEN_STATUSES.includes(status);
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function boundedText(value: unknown, max: number, allowEmpty: boolean): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if ((!allowEmpty && !text) || text.length > max) return null;
  return text;
}

/**
 * The staff selection. Returns the de-duplicated ids or null when the shape
 * is wrong (not an array, empty, too many, non-string ids).
 */
export function parseFeedbackSelection(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  if (!value.every((item) => typeof item === "string" && item.length > 0 && item.length <= 120)) return null;
  const ids = [...new Set(value as string[])];
  if (ids.length < PROMPT_PATCH_LIMITS.minFeedback || ids.length > PROMPT_PATCH_LIMITS.maxFeedback) return null;
  return ids;
}

/** Optional partner note: undefined/null/"" -> null; too long or wrong type -> undefined (invalid). */
export function parsePartnerNote(value: unknown): string | null | undefined {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (text.length > PROMPT_PATCH_LIMITS.noteChars) return undefined;
  return text || null;
}

/**
 * The machine proposal. Every selected podnět must be classified exactly once
 * and nothing else may be; the patch may be empty (all podněty were know-how
 * or platform), but each hunk needs a non-empty `find`.
 */
export function parseProposal(body: unknown, selected: readonly string[]): PromptPatchProposal | null {
  const raw = record(body);
  if (!raw || !Array.isArray(raw.items) || !Array.isArray(raw.patch)) return null;
  const summary = boundedText(raw.summary ?? "", PROMPT_PATCH_LIMITS.summaryChars, true);
  if (summary === null) return null;
  const items: PromptPatchItem[] = [];
  const seen = new Set<string>();
  for (const entry of raw.items) {
    const item = record(entry);
    if (!item || typeof item.feedbackId !== "string" || !selected.includes(item.feedbackId) || seen.has(item.feedbackId)) return null;
    if (!(PROMPT_PATCH_CATEGORIES as readonly string[]).includes(String(item.category))) return null;
    const reason = boundedText(item.reason ?? "", PROMPT_PATCH_LIMITS.reasonChars, true);
    if (reason === null) return null;
    seen.add(item.feedbackId);
    items.push({ feedbackId: item.feedbackId, category: item.category as PromptPatchCategory, reason });
  }
  if (seen.size !== selected.length) return null;
  if (raw.patch.length > PROMPT_PATCH_LIMITS.maxPatches) return null;
  const patch: PromptPatchHunk[] = [];
  for (const entry of raw.patch) {
    const hunk = record(entry);
    if (!hunk || typeof hunk.find !== "string" || typeof hunk.replace !== "string") return null;
    if (!hunk.find.trim() || hunk.find.length > PROMPT_PATCH_LIMITS.patchTextChars || hunk.replace.length > PROMPT_PATCH_LIMITS.patchTextChars) return null;
    const why = boundedText(hunk.why ?? "", PROMPT_PATCH_LIMITS.reasonChars, true);
    if (why === null) return null;
    patch.push({ find: hunk.find, replace: hunk.replace, why });
  }
  return { items, patch, summary };
}

/**
 * The partner's edit of the "po" side. The partner may change only the
 * replacement text: same number of hunks, same `find` in the same order.
 */
export function parseEditedPatch(value: unknown, proposal: PromptPatchProposal): PromptPatchEdit[] | null {
  if (!Array.isArray(value) || value.length !== proposal.patch.length) return null;
  const edits: PromptPatchEdit[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const hunk = record(value[index]);
    if (!hunk || hunk.find !== proposal.patch[index].find || typeof hunk.replace !== "string") return null;
    if (hunk.replace.length > PROMPT_PATCH_LIMITS.patchTextChars) return null;
    edits.push({ find: proposal.patch[index].find, replace: hunk.replace });
  }
  return edits;
}

/** The patch the Mini must apply: the partner's edit wins over the proposal. */
export function effectivePatch(proposal: PromptPatchProposal | null, edited: PromptPatchEdit[] | null): PromptPatchEdit[] {
  if (edited) return edited;
  return proposal ? proposal.patch.map(({ find, replace }) => ({ find, replace })) : [];
}

/** Feedback ids the applied patch worked in: category "prompt" only. */
export function promptCategoryIds(proposal: PromptPatchProposal | null): string[] {
  return proposal ? proposal.items.filter((item) => item.category === "prompt").map((item) => item.feedbackId) : [];
}

function parseJsonValue(value: unknown): unknown {
  if (typeof value !== "string" || !value) return null;
  try { return JSON.parse(value) as unknown; } catch { return null; }
}

export function storedProposal(value: unknown): PromptPatchProposal | null {
  const raw = record(parseJsonValue(value));
  if (!raw || !Array.isArray(raw.items) || !Array.isArray(raw.patch)) return null;
  return {
    items: raw.items as PromptPatchItem[],
    patch: raw.patch as PromptPatchHunk[],
    summary: typeof raw.summary === "string" ? raw.summary : ""
  };
}

export function storedEdits(value: unknown): PromptPatchEdit[] | null {
  const raw = parseJsonValue(value);
  return Array.isArray(raw) ? raw as PromptPatchEdit[] : null;
}

export function storedIds(value: unknown): string[] {
  const raw = parseJsonValue(value);
  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === "string") : [];
}

export interface PromptPatchRow {
  id: string;
  status: string;
  feedback_ids_json: string;
  partner_note: string | null;
  proposal_json: string | null;
  edited_patch_json: string | null;
  result_note: string | null;
  created_at: string;
  updated_at: string;
  decided_at: string | null;
}

/** D1 row -> the reduced DTO the staff card renders. */
export function toPromptPatchDto(row: PromptPatchRow): PromptPatchRequestDto {
  return {
    id: row.id,
    status: isPromptPatchStatus(row.status) ? row.status : "failed",
    feedbackIds: storedIds(row.feedback_ids_json),
    partnerNote: row.partner_note,
    proposal: storedProposal(row.proposal_json),
    editedPatch: storedEdits(row.edited_patch_json),
    resultNote: row.result_note,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    decidedAt: row.decided_at
  };
}
